import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get } from "../api";
import type { Assignable, ReportAction, ReportDetail, Signal, SuggestionList } from "../api";
import TrackForm from "../report/TrackForm";

/** How many rows show before the rest fold away. */
const SHOWN = 6;

type Row = {
  key: string;
  rank: number;
  label: string;
  /** A `.sev` colour for signals, a `.pill` priority for the report's. */
  tone: { cls: string };
  source: string;
  title: string;
  detail?: string;
  meta?: string;
  link: { to: string; text: string };
  action?: ReportAction;
};

/**
 * Everything on the account that wants a person, worst first, in one list.
 *
 * Three sources and no merging between them: a platform signal, a report
 * lowlight and a suggestion about the same campaign stay three rows, each
 * labelled with where it came from. Matching them would be a guess, and a
 * wrong guess hides one of the halves.
 *
 * Only a suggestion can be tracked from here. Signals and lowlights link
 * to the tab that holds their detail, because nothing turns them into a
 * tracked item today -- and a suggestion tracked here is the same promotion
 * the report tab makes (invariant 30), through the same form.
 */
export default function Attention({
  customer,
  signals,
  report,
  suggestions,
  onTracked,
}: {
  customer: string;
  signals: Signal[];
  report: ReportDetail | null;
  suggestions: SuggestionList | null;
  onTracked: () => void;
}) {
  const [all, setAll] = useState(false);
  const [openForm, setOpenForm] = useState<string | null>(null);
  const [users, setUsers] = useState<Assignable[] | null>(null);

  // Who an item could go to, fetched the first time somebody opens a form.
  useEffect(() => {
    if (openForm === null || users !== null) return;
    void get<{ users: Assignable[] }>(`/api/customers/${customer}/assignees`)
      .then((d) => setUsers(d.users))
      .catch(() => setUsers([]));
  }, [openForm, users, customer]);

  const rows = collect(customer, signals, report, suggestions).sort((a, b) => a.rank - b.rank);
  const shown = all ? rows : rows.slice(0, SHOWN);

  return (
    <section className="ov-section" aria-labelledby="ov-attn">
      <div className="ov-head">
        <h2 id="ov-attn">
          Needs attention{rows.length > 0 && <span className="dim"> · {rows.length}</span>}
        </h2>
        <span className="dim">Platform signals, report lowlights and untracked suggestions</span>
      </div>

      {rows.length === 0 ? (
        <p className="empty">Nothing open: no signals, lowlights or untracked suggestions.</p>
      ) : (
        shown.map((r) => (
          <article key={r.key} className="finding ov-attn">
            <div className="ov-attn-top">
              <span className={r.tone.cls}>{r.label}</span>
              <span className="chip tiny">{r.source}</span>
              <h3>{r.title}</h3>
              {r.action && report ? (
                <button
                  className="tiny primary"
                  onClick={() => setOpenForm(openForm === r.key ? null : r.key)}
                  aria-expanded={openForm === r.key}
                >
                  Track
                </button>
              ) : (
                <Link className="btn tiny" to={r.link.to}>
                  {r.link.text}
                </Link>
              )}
            </div>
            {r.detail && <p>{r.detail}</p>}
            {r.meta && <p className="meta">{r.meta}</p>}
            {openForm === r.key && r.action && report && (
              <TrackForm
                action={r.action}
                users={users ?? []}
                customer={customer}
                reportId={report.id}
                onDone={() => {
                  setOpenForm(null);
                  onTracked();
                }}
              />
            )}
          </article>
        ))
      )}

      {rows.length > SHOWN && (
        <button className="linkish" onClick={() => setAll(!all)} aria-expanded={all}>
          {all ? "Show fewer" : `Show ${rows.length - SHOWN} more`}
        </button>
      )}
    </section>
  );
}

const SIGNAL_RANK: Record<Signal["severity"], number> = { critical: 0, serious: 1, warning: 2, info: 4 };
const SIGNAL_LABEL: Record<Signal["severity"], string> = {
  critical: "Critical",
  serious: "Serious",
  warning: "Warning",
  info: "Note",
};
const PRIORITY_RANK: Record<string, number> = { high: 1, medium: 2, low: 3 };

function collect(
  customer: string,
  signals: Signal[],
  report: ReportDetail | null,
  suggestions: SuggestionList | null,
): Row[] {
  const rows: Row[] = signals.map((s, i) => ({
    key: `sig-${s.campaign_id}-${s.kind}-${i}`,
    rank: SIGNAL_RANK[s.severity],
    label: SIGNAL_LABEL[s.severity],
    tone: { cls: `sev ${s.severity}` },
    source: "Platform",
    title: s.describe,
    meta: [
      `${s.metric} · observed ${round(s.observed)} vs baseline ${round(s.baseline)}`,
      s.days_running > 1 ? `${s.days_running} days running` : null,
    ]
      .filter(Boolean)
      .join(" · "),
    link: { to: `/c/${customer}/metrics?campaign=${encodeURIComponent(s.campaign_id)}`, text: "Metrics →" },
  }));

  if (!report) return rows;
  const reportLink = { to: `/c/${customer}/report?r=${report.id}`, text: "Report →" };

  report.report.lowlights.forEach((f, i) => {
    rows.push({
      key: `low-${f.id ?? i}`,
      // Below a serious platform signal, level with a medium suggestion:
      // a lowlight is the agent's reading of a conversation, not a measured
      // break.
      rank: 2.5,
      label: "Lowlight",
      tone: { cls: "pill medium" },
      source: "Report",
      title: f.title,
      detail: f.detail,
      meta: f.carried_from && f.first_seen ? `Raised ${f.first_seen}, still open` : "New in this report",
      link: reportLink,
    });
  });

  // Tracked state is a fact about the tracker, so it comes from
  // `/suggestions` rather than the report row.
  const tracked = new Set(
    (suggestions?.suggestions ?? []).filter((s) => s.tracked_id && s.action_id).map((s) => s.action_id!),
  );
  report.report.action_items
    .filter((a) => a.status === "open" && a.id && !tracked.has(a.id))
    .forEach((a) => {
      rows.push({
        key: `act-${a.id}`,
        rank: PRIORITY_RANK[a.priority] ?? 3,
        label: cap(a.priority),
        tone: { cls: `pill ${a.priority}` },
        source: "Suggestion",
        title: a.title,
        detail: a.detail,
        meta: [
          a.owner ? `Owner ${a.owner}` : null,
          a.due ? `Due ${a.due}` : "No due date",
          a.carried_from && a.first_seen ? `suggested since ${a.first_seen}` : null,
        ]
          .filter(Boolean)
          .join(" · "),
        link: reportLink,
        action: a,
      });
    });

  return rows;
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const round = (v: number): string =>
  Math.abs(v) >= 100 || Number.isInteger(v) ? Math.round(v).toLocaleString() : v.toFixed(2);
